// Base Imports
import React, { Fragment, useEffect, useState } from 'react'
import * as Yup from 'yup'

// Package Imports
import axios from '@services/axiosInterceptorInstance'
import Link from 'next/link'
import Router, { useRouter } from 'next/router'
import { Dialog, Transition } from '@headlessui/react'
import dynamic from 'next/dynamic'

// Component Imports
import { useUI } from '@components/ui/context'
import { handleSubmit } from '../common'
import eventDispatcher from '@components/services/analytics/eventDispatcher'
import { EVENTS_MAP } from '@components/services/analytics/constants'
import ProfileForm from './ProfileForm'
import SubmitButton from '@components/common/SubmitButton'
import ErrorView from '@components/ui/ErrorView'
import AlertRibbon from '@components/ui/AlertRibbon'

// Other Imports
import useDataSubmit from '@commerce/utils/use-data-submit'
import {
  GENERAL_SAVE_CHANGES,
  PROFILE_UPDATED,
} from '@components/utils/textVariables'
import {
  errorSubmitData,
  findByFieldName,
  getErrorMessage,
  parseFullName,
  resetErrorSubmitData,
  resetSubmitData,
  sanitizeDateOrMonth,
  submitData,
} from '@framework/utils/app-util'
import {
  EmptyGuid,
  Messages,
  NEXT_OTP_REQUEST,
  NEXT_VALIDATE_CHANGE_USERNAME_OTP,
  ProfilePageAction,
} from '@components/utils/constants'
import { GA_EVENT } from 'hooks/ga_event.modal'
import { recordGA4Event } from '@components/services/analytics/ga4'
import { Cross } from '@components/icons'
import LoggedOutSection from '../LoggedOutSection'
import { matchStrings } from '@framework/utils/parse-util'
import {
  CHANGE_MOBILE_NO_FORM_FIELDS,
  CHANGE_MOBILE_NO_FORM_ID,
  CHANGE_MOBILE_NUMBER_FORM_VALUES,
  DEFAULT_PROFILE_VALUES,
  PROFILE_FORM_FIELDS,
  PROFILE_FORM_ID,
  PROFILE_FORM_SCHEMA,
} from './helper'
import { getUserNumber } from '@components/account/loginHelper'

let userObj: any

export const CHANGE_MOBILE_NUMBER_FORM_SCHEMA = Yup.object().shape({
  mobileNumber: Yup.string()
    .required(Messages.Validations.Login['MOBILE_NUMBER_REQUIRED'])
    .matches(
      Messages.Validations.RegularExpressions.MOBILE_NUMBER,
      Messages.Validations.Login['MOBILE_NUMBER_INPUT']
    )
    .test(
      'mobileNumber',
      Messages.Validations.Profile['CHANGED_MOBILE_NUMBER_INPUT'],
      (value: string | undefined) => {
        if (value && matchStrings(value.trim(), userObj?.username, true)) {
          return false
        }
        return true
      }
    ),
  otp: Yup.number().min(6),
})

const NewLoginForm = dynamic(() => import('../NewLoginForm'))

interface IProps {
  isShow: boolean
  setShow: (val: boolean) => void
}

export default function MyDetails(props: IProps) {
  const { isShow, setShow } = props
  const [title, setTitle] = useState('My Details')
  const [showChangeMobileNoDialog, setShowChangeMobileNoDialog] =
    useState(false)
  const [isMobileData, setMobileData] = useState<any>()
  const [isOTPSent, setIsOTPSent] = useState(false)
  const { user, setUser, setAlert, displayAlert, isGuestUser } = useUI()
  userObj = user
  const { state: submitState, dispatch: submitDispatch } = useDataSubmit()
  const { CustomerUpdated } = EVENTS_MAP.EVENT_TYPES
  const router = useRouter()
  const { asPath } = router
  const isUserLoggedIn =
    !isGuestUser && user?.userId && user?.userId !== EmptyGuid

  const initialValues = {
    id: user?.userId,
    fullName: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
    mobileNumber: getUserNumber(user),
    email: user?.email,
    birthDate:
      user?.yearOfBirth && user?.monthOfBirth && user?.dayOfBirth
        ? `${user?.yearOfBirth}-${sanitizeDateOrMonth(
            user?.monthOfBirth
          )}-${sanitizeDateOrMonth(user?.dayOfBirth)}`
        : '',
    gender: user?.gender
      ? user?.gender
      : findByFieldName(PROFILE_FORM_FIELDS, 'gender')?.options?.length
      ? ''
      : '',
  }

  useEffect(() => {
    if (!showChangeMobileNoDialog) {
      setIsOTPSent(showChangeMobileNoDialog)
    }
  }, [showChangeMobileNoDialog])

  const changeMobileNumber = (mobile: string) => {
    setShowChangeMobileNoDialog(true)
    setMobileData(mobile)
  }

  const handleSaveProfile = async (data: any) => {
    submitData(submitDispatch, ProfilePageAction.SAVE)

    const name = parseFullName(data?.fullName)
    const userData = {
      userId: user?.userId,
      username: data?.mobileNumber,
      firstName: name?.firstName,
      lastName: name?.lastName ?? '',
      email: data?.email,
      title: '',
      telephone: null,
      mobile: data?.mobileNumber,
      gender: data?.gender,
      dayOfBirth: data?.birthDate ? data?.birthDate?.split('-')[2] : '0',
      monthOfBirth: data?.birthDate ? data?.birthDate?.split('-')[1] : '0',
      yearOfBirth: data?.birthDate ? data?.birthDate?.split('-')[0] : '0',
      birthDate: '',
      postCode: '',
      newsLetterSubscribed: false,
      userSourceType: 1,
      referralType: 'Direct',
      userReferrer: '',
      companyName: '',
      notifyByEmail: false,
      notifyBySMS: true,
      notifyByPost: false,
      registrationSource: '',
    }

    const result = await handleSubmit(userData, user, setUser, setTitle)
    resetSubmitData(submitDispatch)
    if (result) {
      eventDispatcher(CustomerUpdated, {
        entity: JSON.stringify({
          id: user.userId,
          name: user.username,
          dateOfBirth: user.yearOfBirth,
          gender: user.gender,
          email: user.email,
          postCode: user.postCode,
        }),
        entityId: user.userId,
        entityName: user.firstName + user.lastName,
        eventType: CustomerUpdated,
      })
      setAlert({ type: 'success', msg: PROFILE_UPDATED })
    }
    return result
  }

  const handleMobileNoChange = async (data: any) => {
    try {
      if (!isOTPSent) {
        submitData(
          submitDispatch,
          ProfilePageAction.SEND_OTP_FOR_CHANGE_MOBILE_NO
        )
        const otpData = {
          mobileNo: data?.mobileNumber,
          entityType: 1,
          templateId: 1,
        }
        const { data: otpResponse }: any = await axios.post(
          NEXT_OTP_REQUEST,
          otpData
        )
        setIsOTPSent(otpResponse?.success)
        recordGA4Event(window, GA_EVENT.OTP_SENT, {
          page_path: asPath,
        })
        if (otpResponse?.success) {
          setShowChangeMobileNoDialog(data?.mobileNumber ? true : false)
        }

        resetSubmitData(submitDispatch)
      } else {
        submitData(submitDispatch, ProfilePageAction.CHANGE_MOBILE_NO)
        await handleUsernameChangeOTP(data)
      }
    } catch (error) {
      resetSubmitData(submitDispatch)
      console.error(':: error', error)
    }
  }

  const handleUsernameChangeOTP = async (values: any) => {
    const data = {
      mobileNo: values?.mobileNumber,
      otp: values?.otp,
    }
    const { data: validateOTPResult }: any = await axios.post(
      NEXT_VALIDATE_CHANGE_USERNAME_OTP,
      { data: data }
    )
    if (validateOTPResult?.success) {
      const profileData = {
        mobileNumber: values?.mobileNumber,
        fullName: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(),
        email: user?.email,
        gender: user?.gender,
        birthDate:
          user?.yearOfBirth && user?.monthOfBirth && user?.dayOfBirth
            ? `${user?.yearOfBirth}-${sanitizeDateOrMonth(
                user?.monthOfBirth
              )}-${sanitizeDateOrMonth(user?.dayOfBirth)}`
            : '',
      }
      const saveProfileResult = await handleSaveProfile(profileData)
      resetSubmitData(submitDispatch)
      if (saveProfileResult) {
        setShowChangeMobileNoDialog(false)
        Router.reload()
      } else {
        errorSubmitData(submitDispatch, {
          error_code: 'USERNAME_ALREADY_EXISTS',
        })
      }
    } else {
      resetSubmitData(submitDispatch)
      errorSubmitData(submitDispatch, { error_code: 'GENERIC_ERROR' })
    }
  }

  return (
    <>
      <div className="px-6 py-4 mb-4 border-b mob-header sm:hidden">
        <h3 className="max-w-4xl mx-auto text-xl font-semibold text-black">
          <span className="mr-2 leading-none" onClick={() => setShow(true)}>
            <span className="mr-2 leading-none">
              <i className="sprite-icon sprite-left-arrow" />
            </span>
            Profile
          </span>
        </h3>
      </div>
      {isUserLoggedIn ? (
        <div className="w-full profile-container">
          <main className="px-6 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="hidden px-4 sm:px-0 sm:block">
                <h1 className="text-2xl font-semibold tracking-tight capitalize text-primary sm:block sm:text-2xl">
                  {user?.firstName != '' ? (
                    <>
                      {user?.firstName} {user?.lastName}
                    </>
                  ) : (
                    <>Hi</>
                  )}
                </h1>
                <p className="mt-2 text-base text-brown-light sm:block">
                  {getUserNumber(user)} • {user?.email}
                </p>
              </div>
            </div>
            <div>
              <h3 className="max-w-4xl mx-auto mt-6 text-base font-semibold sm:text-xl text-primary">
                Personal Details
              </h3>
              <ProfileForm
                submitState={submitState}
                formId={PROFILE_FORM_ID}
                formFields={PROFILE_FORM_FIELDS}
                schema={PROFILE_FORM_SCHEMA}
                initialValues={initialValues}
                defaultValues={DEFAULT_PROFILE_VALUES}
                onChangeMobileNumber={changeMobileNumber}
                onSubmit={handleSaveProfile}
              />
              <div className="mt-2">
                {displayAlert ? (
                  <div className="mb-3 m-[-24px] w-auto sm:hidden">
                    <AlertRibbon />
                  </div>
                ) : null}
                <div className="flex sm:flex-col1">
                  <SubmitButton
                    formId={PROFILE_FORM_ID}
                    cssClass="w-full flex btn-basic-property items-center justify-center px-4 btn-basic-property -mr-0.5 text-white bg-black border-2 border-black rounded-sm hover:bg-gray-800 hover:text-whitesm:px-6 hover:border-gray-900"
                    submitState={submitState}
                    source={ProfilePageAction.SAVE}
                  >
                    <span className="hidden sm:inline-block font-16">
                      {GENERAL_SAVE_CHANGES}
                    </span>
                    <span className="inline-block sm:hidden">Save Details</span>
                  </SubmitButton>
                </div>
              </div>
            </div>
          </main>
        </div>
      ) : (
        <div className="max-w-4xl mx-auto">
          <LoggedOutSection screen="your profile" />
        </div>
      )}

      <Transition.Root show={showChangeMobileNoDialog ?? false} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setShowChangeMobileNoDialog(false)}
        >
          <div className="fixed inset-0 left-0 bg-black/[.5]" />
          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="fixed inset-y-0 right-0 flex max-w-full pointer-events-none bottom-to-top">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-400"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-400"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="w-screen max-w-md pointer-events-auto">
                    <div className="relative z-50 flex flex-col h-full bg-white shadow-xl mobile-position sm:bg-white">
                      <div className="z-10 px-0 py-0 pb-0 sm:px-0 left-1 top-1">
                        <div className="flex justify-between px-6 py-6 pb-4 mb-0 border-b sm:px-6">
                          <h3 className="text-xl font-bold text-black dark:text-black">
                            Change Mobile Number
                          </h3>
                          <button
                            type="button"
                            className="text-black rounded-md outline-none hover:text-gray-500"
                            onClick={() => {
                              setShowChangeMobileNoDialog(false)
                            }}
                          >
                            <span className="sr-only">Close panel</span>
                            <Cross
                              className="relative top-0 w-7 h-7"
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                      </div>
                      <div className="p-6 py-2 overflow-y-auto">
                        {/* New Login Form start */}
                        <div className="flex justify-between">
                          {!isOTPSent && (
                            <h3 className="my-2 text-sm font-bold text-black">
                              Enter Mobile Number
                            </h3>
                          )}
                          {isOTPSent && (
                            <>
                              <h3 className="my-2 text-sm font-bold text-black">
                                OTP sent on {isMobileData}
                              </h3>
                              <button
                                type="button"
                                className="text-xs text-black opacity-50 pointer-events-none"
                              >
                                Resend in 30s
                              </button>
                              {/* <button type='button' className='text-xs text-orange'>Resend OTP</button> */}
                            </>
                          )}
                        </div>
                        <NewLoginForm
                          isOTPSent={isOTPSent}
                          formId={CHANGE_MOBILE_NO_FORM_ID}
                          formFields={CHANGE_MOBILE_NO_FORM_FIELDS}
                          schema={CHANGE_MOBILE_NUMBER_FORM_SCHEMA}
                          defaultValues={CHANGE_MOBILE_NUMBER_FORM_VALUES}
                          onSubmit={handleMobileNoChange}
                        />

                        {!isOTPSent && (
                          <div className="px-0 py-4 mt-2 chk-btn sm:px-0">
                            <SubmitButton
                              formId={CHANGE_MOBILE_NO_FORM_ID}
                              cssClass="w-full flex items-center justify-center px-4 btn-basic-property -mr-0.5 text-white bg-black border-2 border-black rounded-sm hover:bg-gray-800 hover:text-whitesm:px-6 hover:border-gray-900"
                              submitState={submitState}
                              source={
                                ProfilePageAction.SEND_OTP_FOR_CHANGE_MOBILE_NO
                              }
                            >
                              Request OTP
                            </SubmitButton>
                          </div>
                        )}

                        {isOTPSent && (
                          <div className="px-4 py-4 mt-2 chk-btn sm:px-0">
                            <SubmitButton
                              formId={CHANGE_MOBILE_NO_FORM_ID}
                              cssClass="w-full flex items-center justify-center px-4 btn-basic-property -mr-0.5 text-white bg-black border-2 border-black rounded-sm hover:bg-gray-800 hover:text-whitesm:px-6 hover:border-gray-900"
                              submitState={submitState}
                              source={ProfilePageAction.CHANGE_MOBILE_NO}
                            >
                              Verify OTP
                            </SubmitButton>
                          </div>
                        )}
                        <p className="text-xs dark:text-gray-500">
                          By continuing, I agree to the{' '}
                          <Link
                            legacyBehavior
                            href="/terms-and-conditions"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <a className="text-orange">Terms of use</a>
                          </Link>{' '}
                          &{' '}
                          <Link
                            legacyBehavior
                            href="/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <a className="text-orange">Privacy Policy</a>
                          </Link>
                        </p>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {submitState?.error && (
        <ErrorView
          error={{
            ...submitState?.error,
            ...{
              error_message: getErrorMessage(submitState?.error),
            },
          }}
          closeModal={() => resetErrorSubmitData(submitDispatch)}
        />
      )}
    </>
  )
}
